import DeleteIcon from '@mui/icons-material/Delete'
import {
  Button,
  Checkbox,
  IconButton,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from '@mui/material'
import { useEffect, useState } from 'react'

const withTmpId = f => ({ __tmpId: crypto.randomUUID(), ...f })

const createEmptyField = s => {
  const entityId = s?.entity_id ?? s?.id ?? ''
  return {
    __tmpId: crypto.randomUUID(),
    entity_id: entityId,
    sensor_id: entityId,
    label: '',
    value: '',
    is_visible_in_performance_details: false,
    is_karma_only: false,
  }
}

export type CustomField = {
  entity_id?: string
  sensor_id?: string
  label?: string
  value?: string
  is_visible_in_performance_details?: boolean
  is_karma_only?: boolean
}

export type Sensor = {
  id?: string
  entity_id?: string
  custom_fields?: CustomField[]
}

export type CustomFieldsProps = {
  sensor: Sensor
  onSave: (fields: CustomField[]) => void | Promise<void>
}

export const CustomFields = ({ sensor, onSave }) => {
  const [customFields, setCustomFields] = useState(
    (sensor?.custom_fields ?? []).map(withTmpId)
  )
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setCustomFields((sensor?.custom_fields ?? []).map(withTmpId))
  }, [sensor])

  const updateAt = (id, patch) => {
    setCustomFields(prev =>
      prev.map(f => (f.__tmpId === id ? { ...f, ...patch } : f))
    )
  }

  const addField = () =>
    setCustomFields(prev => [...prev, createEmptyField(sensor)])
  const removeField = id =>
    setCustomFields(prev => prev.filter(f => f.__tmpId !== id))

  const save = async () => {
    const cleaned = customFields.map(({ __tmpId, ...f }) => ({
      ...f,
      label: (f.label || '').trim(),
      value: f.value ?? '',
    }))

    setSaving(true)
    try {
      await onSave(cleaned)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack spacing={2}>
      <TableContainer>
        <Table size={'small'}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Wert</TableCell>
              <TableCell>Sichtbar in Übersicht</TableCell>
              <TableCell>Nur KARMA</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customFields.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ opacity: 0.6 }}>
                  Keine Custom Fields vorhanden
                </TableCell>
              </TableRow>
            ) : (
              customFields.map(f => (
                <TableRow key={f.__tmpId}>
                  <TableCell sx={{ minWidth: 220 }}>
                    <TextField
                      label={'Label'}
                      size={'small'}
                      value={f.label ?? ''}
                      onChange={e =>
                        updateAt(f.__tmpId, { label: e.target.value })
                      }
                      fullWidth
                      sx={{ mt: 1 }}
                    />
                  </TableCell>

                  <TableCell sx={{ minWidth: 220 }}>
                    <TextField
                      label={'Value'}
                      size={'small'}
                      value={f.value ?? ''}
                      onChange={e =>
                        updateAt(f.__tmpId, { value: e.target.value })
                      }
                      fullWidth
                    />
                  </TableCell>

                  <TableCell>
                    <Checkbox
                      checked={!!f.is_visible_in_performance_details}
                      onChange={e =>
                        updateAt(f.__tmpId, {
                          is_visible_in_performance_details: e.target.checked,
                        })
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <Checkbox
                      checked={!!f.is_karma_only}
                      onChange={e =>
                        updateAt(f.__tmpId, {
                          is_karma_only: e.target.checked,
                        })
                      }
                    />
                  </TableCell>

                  <TableCell align={'right'}>
                    <IconButton
                      onClick={() => removeField(f.__tmpId)}
                      size={'small'}
                    >
                      <DeleteIcon fontSize={'small'} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction={'row'} spacing={2}>
        <Button variant={'outlined'} onClick={addField}>
          + Custom Feld hinzufügen
        </Button>

        <Button variant={'contained'} onClick={save} disabled={saving}>
          Speichern
        </Button>
      </Stack>
    </Stack>
  )
}
